import React, { useMemo, useState } from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import _ from 'underscore';
import copyToClipboard from 'copy-to-clipboard';
import notification from 'helpers/notification';
import api from '../../helpers/api';
import sources from '../../helpers/sources';
import Link from '../components/link';
import currencyFormatter from '../../helpers/currency_formatter';
import plural from '../../helpers/plural';
import singularDateUnit from 'helpers/singular_date_unit';
import { MembershipPlan } from '../types/membership_plan';
import Button from '../components/button';
import Switch from '../components/switch';
import { Gym } from '../types/gym';
import {
  displayMissedClassMakeupClassDetails,
  displayMissedClassMakeupClassLimit,
} from 'helpers/membership';
import BlockPlaceholder from 'react/components/block_placeholder';
import joinWithOr from 'helpers/join_with_or';
import useOnMount from 'helpers/use_on_mount';
import useMobileAppSettings from 'helpers/use_mobile_app_settings';
import { multiplierLabelMap } from 'react/pages/account_profile/cancellation_policy_form';

const ShowMembershipPlan = () => {
  const $rootScope = useAngularService('$rootScope');
  const $stateParams = useAngularService('$stateParams');
  const $filter = useAngularService('$filter');
  const $modal = useAngularService('$modal');
  const MembershipIntervalUnits = useAngularService('MembershipIntervalUnits');
  const FeatureNamesService = useAngularService('FeatureNamesService');

  const gym: Gym = $rootScope.gym;
  const mobileAppSettings = useMobileAppSettings();

  const interruptionRequestsAvailable =
    gym.mobile_app_enabled &&
    mobileAppSettings != null &&
    (mobileAppSettings.custom_app ||
      !mobileAppSettings.features_disabled?.includes('cancellation_requests'));

  const [membershipPlan, setMembershipPlan] = useState<MembershipPlan>();
  const [styles, setStyles] = useState([]);
  const [types, setTypes] = useState([]);
  const [copied, setCopied] = useState(false);
  const [paymentOnlyCopied, setPaymentOnlyCopied] = useState(false);

  const getPurchaseLimitText = (limit: number, remaining: number): JSX.Element => {
    const soldOutText = <b>(Sold out)</b>;
    const pluralize = (count: number) => plural('purchase', count, true);

    if (!limit) return <>No limit</>;

    if (remaining === 0 && limit) {
      return (
        <>
          {pluralize(limit)} {soldOutText}
        </>
      );
    }

    const purchased = limit - remaining;
    return (
      <>
        {pluralize(purchased)} - ({remaining} remaining)
      </>
    );
  };

  const getStyles = () => {
    const url = sources.styles(gym.id);
    api.get(url, { count: 1000, slim: 1 }).then((data) => {
      setStyles(data);
    });
  };

  const getEventTypes = () => {
    const url = sources.eventTypes(gym.id);
    api.get(url, { count: 1000, slim: 1 }).then((data) => {
      setTypes(data);
    });
  };

  const getMembershipPlan = () => {
    const url = sources.membershipPlan(gym.id, $stateParams.membershipPlanId);
    api.get(url).then((data) => {
      setMembershipPlan(data);
    });
  };

  useOnMount(() => {
    getMembershipPlan();
    getStyles();
    getEventTypes();
  });

  const selectedStyles = useMemo(() => {
    if (!membershipPlan) return;
    const names = _.chain(styles)
      .filter((style) => membershipPlan.style_ids.indexOf(style.id) >= 0)
      .pluck('name')
      .value();

    const label = names.length === 1 ? 'Attendance limited to' : 'Attendances limited to';

    if (names.length) {
      const withoutStyleLabel = membershipPlan.book_events_with_no_style
        ? '(or classes without a Style)'
        : '';

      return `${label} ${joinWithOr(names)} ${withoutStyleLabel}`;
    }

    if (membershipPlan.book_events_with_no_style) {
      return `${label} classes without a Style`;
    }
  }, [membershipPlan, styles]);

  const selectedTypes = useMemo(() => {
    if (!membershipPlan) return;
    const names = _.chain(types)
      .filter((type) => membershipPlan.event_type_ids.indexOf(type.id) >= 0)
      .pluck('name')
      .value();

    const label = names.length === 1 ? 'Attendance limited to' : 'Attendances limited to';

    if (names.length) {
      const withoutTypeLabel = membershipPlan.book_events_with_no_event_type
        ? '(or Classes without a Type)'
        : '';

      return `${label} ${joinWithOr(names)} ${withoutTypeLabel}`;
    }

    if (membershipPlan.book_events_with_no_event_type) {
      return `${label} Classes without a Type`;
    }
  }, [membershipPlan, types]);

  const scheduleInitialPayment = () => {
    if (!(membershipPlan.cost_upfront > 0)) return null;

    switch (membershipPlan.default_upfront_date_strategy) {
      case 'charge_upfront_amount_on_start_date': {
        return 'Membership start date';
      }
      case 'charge_upfront_amount_asap': {
        return 'As soon as possible';
      }
      case 'charge_upfront_amount_relative_to_start_date': {
        const interval = plural(
          membershipPlan.relative_upfront_date_unit,
          membershipPlan.relative_upfront_date_number,
          true,
        );
        return `${interval} ${membershipPlan.relative_upfront_date_direction} membership starts`;
      }
    }
  };

  const repeatingPaymentStart = () => {
    if (!(membershipPlan.cost_repeat > 0)) return null;

    switch (membershipPlan.default_repeat_date_strategy) {
      case 'start_repeat_payments_one_period_after_upfront': {
        const interval = plural(
          membershipPlan.charge_interval_unit,
          membershipPlan.charge_interval_number,
          true,
        );
        return `${interval} after initial payment`;
      }
      case 'start_repeat_payments_relative_to_start_or_upfront': {
        const interval = plural(
          membershipPlan.relative_repeat_date_unit,
          membershipPlan.relative_repeat_date_number,
          true,
        );
        const occasion = membershipPlan.cost_upfront > 0 ? 'initial payment' : 'membership starts';

        return `${interval} after ${occasion}`;
      }
      case 'start_repeat_payments_on_membership_start_date': {
        return 'Membership start date';
      }
    }
  };

  const bookAndPayAllowedLabel = () => {
    if (!membershipPlan.event_type_ids.length && membershipPlan.book_and_pay_allowed) {
      return 'Payment Only Checkout enabled';
    }

    return 'Book and Pay Checkout enabled';
  };

  const isPaymentOnlyPlan = (plan) => {
    return (
      ['start_on_next_upcoming', 'start_relative_to_purchase_date'].indexOf(
        plan.default_start_date_strategy,
      ) >= 0 ||
      plan.specified_start_date ||
      !plan.restrict_access_by_event_type
    );
  };

  const getSampleUrl = () => {
    const poParam = isPaymentOnlyPlan(membershipPlan) ? '?ct=po' : '';
    return `/websites/${gym.website_domain}/book_and_pay/offers/${membershipPlan.uuid}/carts/sample_complete${poParam}`;
  };

  const bookAndPayAvailable = () => {
    return membershipPlan.restrict_access_by_event_type && !membershipPlan.specified_start_date;
  };

  const memberPortalLabel = FeatureNamesService.memberPortal();

  const memberPortalOrMobileAppEnabled = gym.mobile_app_enabled || gym.member_portal_enabled;

  const bookAndPayUrl = () => {
    return `${window.location.protocol}//${window.location.host}${membershipPlan.book_and_pay_url}`;
  };

  const bookAndPayPaymentOnlyUrl = () => {
    return `${window.location.protocol}//${window.location.host}${membershipPlan.book_and_pay_payment_only_url}`;
  };

  const manageTypes = () => {
    $modal.open({
      template: require('templates/gyms/calendar/events/manage_types.html.slim'),
      controller: 'ManageEventTypesModal',
    });
  };

  const manageBookAndPay = () => {
    const modalInstance = $modal.open({
      template: require('templates/gyms/membership_plans/manage_membership_plan_book_and_pay_modal.html.slim'),
      resolve: {
        plan: () => {
          return { ...membershipPlan };
        },
      },
      controller: 'ManageMembershipPlanBookAndPayCtrl',
    });

    modalInstance.result.then(function () {
      return getMembershipPlan();
    });
  };

  const savePlan = (membership_plan) => {
    const url = sources.membershipPlan(gym.id, $stateParams.membershipPlanId);
    api.put(url, { membership_plan }).then((plan) => {
      setMembershipPlan(plan);
    });
  };

  const getType = (id) => {
    return _.find(types, (type) => type.id === id);
  };

  const getTypeLimits = (id) => {
    return _.find(membershipPlan.per_event_type_limits, (limit) => limit.event_type_id === id);
  };

  const getLimitString = (id) => {
    const limits = getTypeLimits(id);

    if (!limits) return 'Unlimited';

    const classes = plural('classes', limits.limit, true);

    const period =
      limits.date_range_number > 1
        ? `every ${limits.date_range_number} ${limits.date_range_unit}`
        : `per ${singularDateUnit(limits.date_range_unit)}`;

    return `${classes} ${period}`;
  };

  if (!membershipPlan) return <BlockPlaceholder />;

  return (
    <div className="content-section manage-memberships">
      <header className="page-header">
        <h1 className="page-header__title">Membership plan: {membershipPlan.label}</h1>
        <div className="page-header__buttons">
          <Link type="button" variant="hollow" route="gym.membership-plans.list">
            Back
          </Link>
          <Link
            className="btn btn-primary"
            route="gym.membership-plans.edit"
            routeParams={{ membershipPlanId: membershipPlan.id }}
          >
            Edit
          </Link>
        </div>
      </header>

      <main className="container">
        <table className="table worx-table worx-table--no-border-top">
          <tbody>
            <tr>
              <td style={{ width: '40%' }}>Category</td>
              <td className="is-primary">{membershipPlan.category?.label}</td>
            </tr>
            <tr>
              <td>Privileges</td>
              <td className="is-primary">
                <ul className="list-unstyled last-one">
                  {membershipPlan.classes_unlimited && <li>Unlimited classes</li>}
                  {membershipPlan.restrict_future_bookings_by_count && (
                    <li>
                      Allows at most {membershipPlan.future_bookings_count_limit} future{' '}
                      {plural('bookings', membershipPlan.future_bookings_count_limit, true)} per
                      membership
                    </li>
                  )}
                  {membershipPlan.restrict_future_bookings_by_date ? (
                    <li>
                      Allows bookings for classes up to{' '}
                      {plural(
                        membershipPlan.future_booking_term_unit,
                        membershipPlan.future_booking_term_number,
                        true,
                      )}{' '}
                      in the future
                    </li>
                  ) : null}
                  {!membershipPlan.classes_unlimited && membershipPlan.classes_limit > 0 ? (
                    <li>
                      {membershipPlan.classes_limit} class(es)
                      {membershipPlan.classes_interval_number > 0 && (
                        <p>
                          every {membershipPlan.classes_interval_number}{' '}
                          {MembershipIntervalUnits[membershipPlan.classes_interval_unit]}
                        </p>
                      )}
                    </li>
                  ) : null}
                </ul>
              </td>
            </tr>
            <tr>
              <td>When classes are missed</td>
              <td className="is-primary">
                {displayMissedClassMakeupClassDetails(membershipPlan)}
                <div>{displayMissedClassMakeupClassLimit(membershipPlan)}</div>
              </td>
            </tr>
            <tr>
              <td>Expiration date</td>
              {membershipPlan.term_number > 0 ? (
                <td className="is-primary">
                  {membershipPlan.term_number} {MembershipIntervalUnits[membershipPlan.term_unit]}
                </td>
              ) : (
                <td className="is-primary">None</td>
              )}
            </tr>
            <tr>
              <td>Recurring payment amount</td>
              <td className="is-primary">
                {$filter('membershipPlanPaymentFilter')(membershipPlan)}
              </td>
            </tr>
            <tr>
              <td>Initial payment amount</td>
              <td className="is-primary">
                {$filter('membershipPlanJoiningFeeFilter')(membershipPlan.cost_upfront)}
              </td>
            </tr>
            <tr>
              <td>Membership starts</td>
              <td className="is-primary">{$filter('membershipPlanStartDate')(membershipPlan)}</td>
            </tr>
            {scheduleInitialPayment() ? (
              <tr>
                <td>Schedule initial payment for</td>
                <td className="is-primary">{scheduleInitialPayment()}</td>
              </tr>
            ) : null}
            {repeatingPaymentStart() ? (
              <tr>
                <td>Repeating payments start</td>
                <td className="is-primary">{repeatingPaymentStart()}</td>
              </tr>
            ) : null}
            <tr>
              <td>Trial membership?</td>
              <td className="is-primary">{membershipPlan.trial ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>Allow purchase from Kiosk Mode?</td>
              <td className="is-primary">
                {membershipPlan.purchase_from_kiosk_mode ? 'Yes' : 'No'}
              </td>
            </tr>
            {memberPortalOrMobileAppEnabled ? (
              <tr>
                <td>Allow purchase from {memberPortalLabel}?</td>
                <td className="is-primary">
                  {membershipPlan.allow_membership_purchase_from_member_portal ? 'Yes' : 'No'}
                </td>
              </tr>
            ) : null}

            <tr>
              <td>Allow Clubworx users to create memberships using this plan?</td>
              <td className="is-primary">
                {membershipPlan.show_when_adding_membership_internally ? 'Yes' : 'No'}
              </td>
            </tr>

            <tr>
              <td>Purchase limit</td>
              <td className="is-primary">
                {getPurchaseLimitText(
                  membershipPlan.purchase_limit,
                  membershipPlan.purchases_remaining,
                )}
              </td>
            </tr>

            <tr>
              <td>Restrict access by Event Type</td>
              <td className="is-primary">{selectedTypes || 'No restriction'}</td>
            </tr>

            {membershipPlan.event_type_ids.map((id) => (
              <tr key={id}>
                <td>Attendance limit for {getType(id)?.name} classes</td>
                <td className="is-primary">{getLimitString(id)}</td>
              </tr>
            ))}

            {membershipPlan.book_events_with_no_event_type && (
              <tr>
                <td>Attendance limit for Classes without a Type</td>
                <td className="is-primary">Unlimited</td>
              </tr>
            )}

            {gym.is_martial_arts ? (
              <tr>
                <td>Restrict access by Style</td>
                <td className="is-primary">{selectedStyles || 'No restriction'}</td>
              </tr>
            ) : null}
            {gym.gym_config.recurring_bookings_feature_enabled ? (
              <tr>
                <td>Allow membership to be used for Recurring Bookings?</td>
                <td className="is-primary">
                  {membershipPlan.allow_recurring_bookings ? 'Yes' : 'No'}
                </td>
              </tr>
            ) : null}
            <tr>
              <td>Send receipt email to member on successful payment?</td>
              <td className="is-primary">{membershipPlan.send_invoice_receipts ? 'Yes' : 'No'}</td>
            </tr>
            <tr>
              <td>Tax status for membership payments</td>
              <td className="is-primary">
                {$filter('membershipTaxStatus')(membershipPlan.tax_status)}
              </td>
            </tr>
            {gym.has_payment_provider ? (
              <tr>
                <td>Automatic reschedule on failure</td>
                <td className="is-primary">
                  {membershipPlan.automatic_reschedule_days ? (
                    <span>
                      After {membershipPlan.automatic_reschedule_days}{' '}
                      {plural('day', membershipPlan.automatic_reschedule_days)}
                      {membershipPlan.automatic_reschedule_limit && (
                        <span> (limit: {membershipPlan.automatic_reschedule_limit})</span>
                      )}
                    </span>
                  ) : (
                    <span>Disabled</span>
                  )}
                </td>
              </tr>
            ) : null}
            {gym.has_payment_provider ? (
              <tr>
                <td>Failure fee</td>
                <td className="is-primary">
                  {!membershipPlan.failure_fee_amount && <span>No failure fee</span>}
                  {membershipPlan.failure_fee_schedule_strategy ===
                    'bundle_failure_fee_with_next_payment' && (
                    <span>
                      {currencyFormatter(membershipPlan.failure_fee_amount, gym.currency_symbol)}{' '}
                      bundled with next payment
                    </span>
                  )}
                  {membershipPlan.failure_fee_schedule_strategy === 'use_failure_fee_lead_days' && (
                    <span>
                      {currencyFormatter(membershipPlan.failure_fee_amount, gym.currency_symbol)}{' '}
                      charged after {membershipPlan.failure_fee_lead_days}{' '}
                      {plural('day', membershipPlan.failure_fee_lead_days)}
                    </span>
                  )}
                </td>
              </tr>
            ) : null}
            <tr>
              <td>Cancellation policy</td>
                <td className="is-primary">
                  <div>
                    Cancellation of this membership plan:
                    <ul>
                      {interruptionRequestsAvailable && (
                        <>
                          <li className="margin-bottom-8">
                            {membershipPlan.allow_cancellation_requests_from_mobile_app
                              ? membershipPlan.mobile_app_cancellation_requests_require_review
                                ? 'Can be requested by members through the mobile app, but will require approval in Clubworx before being processed.'
                                : 'Can be requested by members through the mobile app, and will be approved automatically without review.'
                              : 'May not be requested by members from the mobile app.'}
                          </li>

                          {membershipPlan.allow_cancellation_requests_from_mobile_app &&
                            membershipPlan.allow_cancellation_requests_from_mobile_app_outside_of_policy && (
                              <li className="margin-bottom-8">
                                {membershipPlan.mobile_app_cancellation_requests_require_review
                                  ? 'Will be allowed from the mobile app, even if the request is outside the bounds of the cancellation policy — however these requests will require review/approval.'
                                  : 'Will be allowed from the mobile app, even if the request is outside the bounds of the cancellation policy.'}
                              </li>
                            )}
                        </>
                      )}

                      <li className="margin-bottom-8">
                        {(() => {
                          const notice =
                            membershipPlan.mobile_app_cancellation_notice_period_number;
                          const minTerm =
                            membershipPlan.mobile_app_cancellation_minimum_term_number;
                          if (notice > 0 && minTerm > 0) {
                            return `Require ${notice} ${plural(
                              membershipPlan.mobile_app_cancellation_notice_period_unit,
                              notice,
                            )} notice and ${minTerm} ${plural(
                              membershipPlan.mobile_app_cancellation_minimum_term_unit,
                              minTerm,
                            )} of the membership to have elapsed prior to the cancellation date.`;
                          } else if (notice > 0) {
                            return `Require ${notice} ${plural(
                              membershipPlan.mobile_app_cancellation_notice_period_unit,
                              notice,
                            )} notice before the cancellation date.`;
                          } else if (minTerm > 0) {
                            return `Require ${minTerm} ${plural(
                              membershipPlan.mobile_app_cancellation_minimum_term_unit,
                              minTerm,
                            )} of the membership to have elapsed prior to the cancellation date.`;
                          } else {
                            return 'Can be processed at any time.';
                          }
                        })()}
                      </li>

                      <li className="margin-bottom-8">
                        {(() => {
                          const fee = Number(
                            membershipPlan.fee_for_mobile_app_cancellations || 0,
                          ).toFixed(2);
                          if (Number(membershipPlan.fee_for_mobile_app_cancellations || 0) <= 0) {
                            return 'Incurs no cancellation fee.';
                          } else if (
                            membershipPlan.mobile_app_cancel_fee_default_due_date_strategy ===
                            'bundle_cancel_fee_with_final_payment_or_on_cancel_date'
                          ) {
                            return `Will incur a ${gym.currency_symbol}${fee} cancellation fee, charged with the final membership payment (or on the cancellation date, if there is no final payment).`;
                          } else {
                            const mult =
                              membershipPlan.mobile_app_cancel_fee_default_due_date_multiplier;
                            const num =
                              membershipPlan.mobile_app_cancel_fee_default_due_date_number;
                            const unit = membershipPlan.mobile_app_cancel_fee_default_due_date_unit;
                            const anchorMap = {
                              schedule_cancel_fee_relative_to_request_approval:
                                'cancellation approved',
                              schedule_cancel_fee_relative_to_cancellation_date:
                                'cancellation date',
                              schedule_cancel_fee_relative_to_final_payment: 'final payment date',
                            };
                            const anchor =
                              anchorMap[
                                membershipPlan.mobile_app_cancel_fee_default_due_date_anchor
                              ] || '';
                            const multLabel = multiplierLabelMap[mult] || '';
                            if (mult !== 'schedule_cancel_fee_multiplier_on') {
                              return `Will incur a ${gym.currency_symbol}${fee} cancellation fee, charged ${num} ${plural(unit, num)} ${multLabel} ${anchor}.`;
                            } else {
                              return `Will incur a ${gym.currency_symbol}${fee} cancellation fee, charged on ${anchor}.`;
                            }
                          }
                        })()}
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            {gym.book_and_pay_enabled ? (
              <tr>
                <td>{bookAndPayAllowedLabel()}</td>
                <td>
                  {membershipPlan.book_and_pay_allowed ? (
                    <div>
                      <Switch
                        checked={membershipPlan.book_and_pay_enabled}
                        onChange={(book_and_pay_enabled) => {
                          savePlan({ book_and_pay_enabled });
                        }}
                      />{' '}
                      <Link route="gym.membership-plans.book_and_pay">Go to checkout settings</Link>
                    </div>
                  ) : (
                    <div>
                      <strong>Not available</strong>

                      {membershipPlan.default_upfront_date_strategy ===
                      'charge_upfront_amount_relative_to_start_date' ? (
                        <div>
                          Checkout functionality is not available for plans set to take an initial
                          payment before the membership starts.
                        </div>
                      ) : (
                        <div>
                          Checkout functionality is only available for membership plans that are set
                          to take a single payment only.
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ) : null}
            {gym.book_and_pay_enabled && membershipPlan.book_and_pay_enabled ? (
              <>
                <tr>
                  <td>Checkout enabled on Website Calendar</td>
                  <td>
                    {membershipPlan.event_type_ids.length ? (
                      <div>
                        <Switch
                          checked={membershipPlan.allow_book_and_pay_from_calendar}
                          disabled={!membershipPlan.book_and_pay_allowed}
                          onChange={(allow_book_and_pay_from_calendar) => {
                            savePlan({ allow_book_and_pay_from_calendar });
                          }}
                        />{' '}
                        <Link route="gym.membership-plans.book_and_pay">
                          Go to checkout settings
                        </Link>
                        <div className="margin-top-8">
                          If enabled, it will be possible to purchase this membership and book into
                          classes from the website calendar.
                        </div>
                      </div>
                    ) : (
                      <div>
                        <strong>Not available</strong>
                        <br />
                        To use this setting, consider adding some{' '}
                        <Button variant="link" onClick={() => manageTypes()}>
                          event types
                        </Button>
                        <span> and linking them to your events.</span>
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Book and Pay Checkout URL</td>
                  <td>
                    {bookAndPayAvailable() ? (
                      <div>
                        <a
                          className="btn btn-xs btn-primary"
                          href={bookAndPayUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Book &amp; Pay Checkout
                        </a>
                        <button
                          className="btn btn-xs btn-success"
                          type="button"
                          onClick={() => {
                            copyToClipboard(bookAndPayUrl());
                            notification.success('Copied to clipboard');
                            setCopied(true);
                            setTimeout(() => {
                              setCopied(false);
                            }, 1500);
                          }}
                        >
                          {copied ? 'Copied' : 'Copy URL'}
                        </button>
                        <div className="margin-top-8">
                          <strong>Book &amp; Pay Checkout</strong> allows customers to book into a
                          class and pay for it at the same time. It's the perfect option for
                          pay-as-you-go classes.
                        </div>
                      </div>
                    ) : (
                      <div>
                        <strong>Not available</strong>
                        <br />
                        To enable Book &amp; Pay Checkout, a membership plan must be set to take a
                        single payment only, and it must also be restricted by one or more event
                        types.
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Payment Only Checkout URL</td>
                  <td>
                    <a
                      className="btn btn-xs btn-primary"
                      href={bookAndPayPaymentOnlyUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open Payment-Only Checkout
                    </a>
                    <button
                      className="btn btn-xs btn-success"
                      type="button"
                      onClick={() => {
                        copyToClipboard(bookAndPayPaymentOnlyUrl());
                        notification.success('Copied to clipboard');
                        setPaymentOnlyCopied(true);
                        setTimeout(() => {
                          setPaymentOnlyCopied(false);
                        }, 1500);
                      }}
                    >
                      {paymentOnlyCopied ? 'Copied' : 'Copy URL'}
                    </button>
                    <div className="margin-top-8">
                      <strong>Payment-Only Checkout</strong> makes it easy to accept one-off
                      payments from customers in real time. It also allows you to sign up new
                      customers who don't need to book a class right now. Payment-Only Checkout
                      makes it easy to accept one-off payments from customers in real time. It
                      allows you to sign up customers on the spot who don't need to attend a class
                      at the same time.
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>After Successful Checkout Purchase</td>
                  <td>
                    {membershipPlan.uses_default_book_and_pay_complete_page_settings
                      ? 'Return to calendar / new purchase page'
                      : 'Redirect to custom URL'}

                    <div className="margin-top-4">
                      <Button variant="link" onClick={() => manageBookAndPay()}>
                        Manage receipt page settings
                      </Button>
                      <br />
                      <a href={getSampleUrl()} target="_blank" rel="noopener noreferrer">
                        View sample receipt
                      </a>
                    </div>
                  </td>
                </tr>
              </>
            ) : null}
          </tbody>
        </table>
      </main>
    </div>
  );
};

angular.module('gym').value('ShowMembershipPlan', ShowMembershipPlan);
